import { Spacing } from '@persona-id/design-tokens';
import { ReactNode } from 'react';
import { Draggable, DraggableChildrenFn } from 'react-beautiful-dnd';
import styled from 'styled-components';

import { StyledIconDrag } from 'dashboard/lib/dnd/StyledIconDrag';
import useToggle from 'lib/hooks/useToggle';

const DndWrapper = styled('div')<{ disableDrag?: boolean }>`
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};
  padding: ${Spacing[3]} ${Spacing[2]};

  display: grid;
  grid-template-columns: ${({ disableDrag }) => (!disableDrag ? 'auto 1fr auto' : '1fr auto auto')};
  gap: ${Spacing[4]};
  align-items: center;
`;

const SuffixContainer = styled('div')`
  margin-left: auto;
`;

interface DndContainerProps {
  children: ReactNode;
  isDragging: boolean;
  suffix?: ReactNode;
  className?: string;
  disableDrag?: boolean;
  dataTest?: string;
}

const DndContainer = ({
  children,
  isDragging,
  suffix,
  className,
  dataTest,
  disableDrag,
}: DndContainerProps) => {
  const [isMouseOver, { enable: handleMouseEnter, disable: handleMouseLeave }] = useToggle(false);

  return (
    <DndWrapper
      className={className}
      style={{ background: 'white' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      disableDrag={disableDrag}
      data-test={dataTest}
    >
      {!disableDrag && <StyledIconDrag $active={isDragging} $hover={isMouseOver} />}
      {children}
      {suffix && <SuffixContainer>{suffix}</SuffixContainer>}
    </DndWrapper>
  );
};

interface DraggableListItemProps {
  draggableId: string;
  index: number;
  children: ReactNode | DraggableChildrenFn;
  suffix?: ReactNode;
  className?: string;
  disableDrag?: boolean;
  dataTest?: string;
}

const DraggableListItem = ({
  draggableId,
  index,
  children,
  suffix,
  className,
  disableDrag,
  dataTest,
}: DraggableListItemProps) => {
  return (
    <Draggable draggableId={draggableId} index={index} isDragDisabled={disableDrag}>
      {(provided, snapshot, rubric) => {
        return (
          <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
            <DndContainer
              className={className}
              dataTest={dataTest}
              isDragging={snapshot.isDragging}
              disableDrag={disableDrag}
              suffix={suffix}
            >
              {typeof children === 'function' ? children(provided, snapshot, rubric) : children}
            </DndContainer>
          </div>
        );
      }}
    </Draggable>
  );
};

export default DraggableListItem;
